import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Calendar, Clock, MapPin, Users, Star, Globe, ChevronRight, BookOpen, Award, Target, User, Mail, Menu, X } from 'lucide-react';

const Index = () => {
  const [language, setLanguage] = useState<'pl' | 'en'>('pl');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const content = {
    pl: {
      nav: {
        home: 'Strona główna',
        experts: 'Eksperci',
        schedule: 'Harmonogram',
        topics: 'Tematy',
        pricing: 'Dołącz do kursu: Rezerwacja i Inwestycja',
        codeOfConduct: 'Regulamin',
        contact: 'Kontakt'
      },
      hero: {
        title: 'IB DP Accelerator',
        subtitle: 'Twoja prosta droga do 45 punktów',
        description: 'Intensywne kursy powtórkowe dla uczniów DP Year 2. Matura międzynarodowa IB to wyzwanie, w którym liczy się nie tylko wiedza, ale i strategia. IB DP Accelerator to program zaprojektowany, aby w krótkim czasie zamienić stres w pewność siebie i zapewnić Ci najwyższy wyniki! Zadbaj o swój komfort nauki w sprzyjającym otoczeniu. Nasze kursy to połączenie merytoryki z motywującą atmosferą.',
        when: 'Kiedy: Ferie zimowe (2–13 lutego 2026). To ostatni moment na kompleksowe uporządkowanie materiału przed finalną prostą.',
        where: 'Gdzie: Nowoczesne i komfortowe sale wykładowe WorldLINK Foundation Poland (Kraków-Wieliczka).',
        how: 'Jak: Wyłącznie zajęcia stacjonarne. Wierzymy w siłę interakcji – bezpośredni kontakt z MEGA DOŚWIADCZONYMI nauczycielami i wspólna praca w grupach dają efekty, których nie zastąpi żaden kurs online.',
        motto: 'Maksymalny wynik! 100 dni do matury IB. Eksperci i strategia – zero przypadków!',
        cta: 'Zapisz się teraz'
      },
      features: {
        title: 'Dlaczego IB DP Accelerator?',
        items: [
          {
            icon: Award,
            title: '20+ Lat Doświadczenia',
            description: 'Twój bilet do najlepszych uczelni świata. 🌍 Stacjonarne kursy prowadzone przez polsko-brytyjski zespół EKSPERTÓW i doświadczonych NAUCZYCIELI. To nie są zwykłe lekcje – to mentoring oparty na wieloletnim sukcesie w edukacji międzynarodowej. 🏆 Celujemy w "7": Nasi podopieczni to liderzy rankingów maturalnych. ✍️ IA & EE Mastery: Pomagamy przekuć Twoje badania w oceny „A”.'
          },
          {
            icon: Target,
            title: 'Twoja droga do ocen „7” z egzaminów IB',
            description: 'Uczymy Cię nie tylko treści, ale i najlepszych strategii egzaminacyjnych, aby zmaksymalizować Twój wynik w sesji Maj 2026!'
          },
          {
            icon: BookOpen,
            title: 'Autorskie Materiały',
            description: 'Otrzymujesz wyłącznie autorskie, sprawdzone materiały udoskonalane przez lata pracy z najlepszymi uczniami IB.'
          }
        ]
      },
      courses: {
        title: '📍 SESJA LUTY 2026 🚀 Program „Road to 7” 🎓 Autorskie seminaria maturalne ✨ Eksperckie kursy stacjonarne',
        subtitle: 'Wybierz z kursów specjalistycznych (2,5 dnia każdy)',
        items: [
          {
            name: 'Biology SL',
            date: '2-4.02.2026',
            description: 'Biologia Poziom Standardowy - dr David O\'Keeffe'
          },
          {
            name: 'Biology HL',
            date: '4-6.02.2026',
            description: 'Biologia Poziom Wyższy - dr David O\'Keeffe'
          },
          {
            name: 'Geography SL',
            date: '2.02 (13:30) - 4.02.2026',
            description: 'Geografia Poziom Standardowy - mgr Anna Krzemińska-Kaczyńska'
          },
          {
            name: 'Geography HL',
            date: '5-6.02.2026',
            description: 'Geografia Poziom Wyższy - mgr Anna Krzemińska-Kaczyńska'
          },
          {
            name: 'Mathematics AA SL',
            date: '9-11.02.2026',
            description: 'Matematyka Analysis & Approaches SL - dr Bill Roberts'
          },
          {
            name: 'Mathematics AA HL',
            date: '11-13.02.2026',
            description: 'Matematyka Analysis & Approaches HL - dr Bill Roberts'
          }
        ]
      },
      pricing: {
        title: 'Cennik',
        subtitle: 'Gwarancja jakości: grupy maksymalnie 10 osób!',
        single: {
          title: '1 Kurs (2,5 dnia)',
          price: '600 GBP',
          priceGBP: '2900 PLN',
          includes: [
            '15 godzin sesji powtórkowych',
            'Autorskie materiały ekspertów',
            'Sesje CAS & Wellbeing',
            'Profesjonalna opieka'
          ]
        },
        double: {
          title: '2 Kursy (5 dni) - Rekomendowany',
          price: '1200 GBP',
          priceGBP: '5800 PLN',
          includes: [
            '30 godzin sesji powtórkowych',
            'Autorskie materiały ekspertów',
            'Sesje CAS & Wellbeing',
            'Wspólne śniadania',
            'Profesjonalna opieka'
          ]
        }
      }
    },
    en: {
      nav: {
        home: 'Home',
        experts: 'Experts',
        schedule: 'Schedule',
        topics: 'Topics',
        pricing: 'Join the DP Accelerator: Enrollment & Investment',
        codeOfConduct: 'Code of Conduct',
        contact: 'Contact'
      },
      hero: {
        title: 'IB DP Accelerator',
        subtitle: 'Your straightforward path to 45 points',
        description: 'Intensive review courses for DP Year 2 students. The IB Diploma Programme is a challenge where not only knowledge counts, but also strategy. IB DP Accelerator is a program designed to quickly transform stress into confidence and ensure your highest results! Take care of your learning comfort in a supportive environment. Our courses combine academic content with a motivating atmosphere.',
        when: 'When: Winter Break (February 2–13, 2026). This is the last moment for comprehensive material organization before the final stretch.',
        where: 'Where: Modern and comfortable lecture halls at WorldLINK Foundation Poland (Krakow-Wieliczka).',
        how: 'How: Exclusively in-person classes. We believe in the power of interaction – direct contact with MEGA EXPERIENCED teachers and collaborative group work deliver results that no online course can replace.',
        motto: 'Maximum score! 100 days to IB graduation. Experts and strategy – zero chance!',
        cta: 'Register Now'
      },
      features: {
        title: 'Why IB DP Accelerator?',
        items: [
          {
            icon: Award,
            title: '20+ Years Experience',
            description: 'Your ticket to the world\'s best universities. 🌍 In-person courses led by a Polish-British team of EXPERTS and experienced TEACHERS. These are not ordinary lessons – this is mentoring based on years of success in international education. 🏆 We aim for "7": Our students are leaders in graduation rankings. ✍️ IA & EE Mastery: We help transform your research into "A" grades.'
          },
          {
            icon: Target,
            title: 'Your path to Grade "7" in IB exams',
            description: 'We teach you not only content, but also the best exam strategies to maximize your score in May 2026!'
          },
          {
            icon: BookOpen,
            title: 'Proprietary Materials',
            description: 'Our students receive access to exclusive, proprietary resources extensively field-tested to ensure maximum exam efficacy.'
          }
        ]
      },
      courses: {
        title: 'Exclusive In-Person Courses: February 2026 Session',
        subtitle: 'Choose from specialist courses (2.5 days each)',
        items: [
          {
            name: 'Biology SL',
            date: '2-4.02.2026',
            description: 'Biology Standard Level - Dr David O\'Keeffe'
          },
          {
            name: 'Biology HL',
            date: '4-6.02.2026',
            description: 'Biology Higher Level - Dr David O\'Keeffe'
          },
          {
            name: 'Geography SL',
            date: '2.02 (13:30) - 4.02.2026',
            description: 'Geography Standard Level - Anna Krzemińska-Kaczyńska'
          },
          {
            name: 'Geography HL',
            date: '5-6.02.2026',
            description: 'Geography Higher Level - Anna Krzemińska-Kaczyńska'
          },
          {
            name: 'Mathematics AA SL',
            date: '9-11.02.2026',
            description: 'Mathematics Analysis & Approaches SL - Dr Bill Roberts'
          },
          {
            name: 'Mathematics AA HL',
            date: '11-13.02.2026',
            description: 'Mathematics Analysis & Approaches HL - Dr Bill Roberts'
          }
        ]
      },
      pricing: {
        title: 'Pricing',
        subtitle: 'Quality guarantee: groups of maximum 10 people!',
        single: {
          title: '1 Course (2.5 days)',
          price: '600 GBP',
          priceGBP: '2900 PLN',
          includes: [
            '15 hours of review sessions',
            'Expert proprietary materials',
            'CAS & Wellbeing sessions',
            'Professional supervision'
          ]
        },
        double: {
          title: '2 Courses (5 days) - Recommended',
          price: '1200 GBP',
          priceGBP: '5800 PLN',
          includes: [
            '30 hours of review sessions',
            'Expert proprietary materials',
            'CAS & Wellbeing sessions',
            'Shared breakfasts',
            'Professional supervision'
          ]
        }
      }
    }
  };

  const t = content[language];

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="border-b bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/60 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-yellow-500 rounded-full flex items-center justify-center">
              <span className="text-white font-bold text-sm">IB</span>
            </div>
            <span className="font-bold text-xl">DP Accelerator</span>
          </div>
          
          <div className="hidden md:flex space-x-6">
            <Link to="/" className="hover:text-blue-600 transition-colors">{t.nav.home}</Link>
            <Link to="/experts" className="hover:text-blue-600 transition-colors">{t.nav.experts}</Link>
            <Link to="/schedule" className="hover:text-blue-600 transition-colors">{t.nav.schedule}</Link>
            <Link to="/course-topics" className="hover:text-blue-600 transition-colors">{t.nav.topics}</Link>
            <Link to="/pricing" className="hover:text-blue-600 transition-colors">{t.nav.pricing}</Link>
            <Link to="/code-of-conduct" className="hover:text-blue-600 transition-colors">{t.nav.codeOfConduct}</Link>
            <Link to="/contact" className="hover:text-blue-600 transition-colors">{t.nav.contact}</Link>
          </div>
          
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setLanguage(language === 'pl' ? 'en' : 'pl')}
              className="flex items-center space-x-1"
            >
              <Globe className="w-4 h-4" />
              <span>{language === 'pl' ? 'EN' : 'PL'}</span>
            </Button>
            
            {/* Mobile menu button */}
            <Button
              variant="outline"
              size="sm"
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
            </Button>
          </div>
        </div>
        
        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-b shadow-lg">
            <div className="container mx-auto px-4 py-4 space-y-4">
              <Link 
                to="/" 
                className="block hover:text-blue-600 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t.nav.home}
              </Link>
              <Link 
                to="/experts" 
                className="block hover:text-blue-600 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t.nav.experts}
              </Link>
              <Link 
                to="/schedule" 
                className="block hover:text-blue-600 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t.nav.schedule}
              </Link>
              <Link 
                to="/course-topics" 
                className="block hover:text-blue-600 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t.nav.topics}
              </Link>
              <Link 
                to="/pricing" 
                className="block hover:text-blue-600 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t.nav.pricing}
              </Link>
              <Link 
                to="/code-of-conduct" 
                className="block hover:text-blue-600 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t.nav.codeOfConduct}
              </Link>
              <Link 
                to="/contact" 
                className="block hover:text-blue-600 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t.nav.contact}
              </Link>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative py-20 bg-gradient-to-br from-blue-600 via-blue-700 to-yellow-500 text-white overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="mb-4 text-sm md:text-base text-blue-200 font-medium">
              #EducationExcellence #QualityTourism #TimeLeverage #ValuesDrivenEducation #TrustInTeaching #SustainablePerformance
            </div>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-white to-yellow-200 bg-clip-text text-transparent">
              {t.hero.title}
            </h1>
            {/* Main Subtitle with Highlight */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 mb-8 border border-white/20">
              <h2 className="text-2xl md:text-3xl font-bold text-yellow-200 mb-4">
                {t.hero.subtitle}
              </h2>
              <p className="text-lg md:text-xl text-blue-100 leading-relaxed mb-6">
                {t.hero.description}
              </p>
            </div>
            
            {/* Key Information Cards */}
            <div className="grid md:grid-cols-3 gap-4 mb-8 max-w-5xl mx-auto">
              <div className="bg-gradient-to-br from-yellow-400/20 to-yellow-600/20 backdrop-blur-sm rounded-xl p-4 border border-yellow-300/30">
                <div className="flex items-center space-x-3 mb-3">
                  <Calendar className="w-6 h-6 text-yellow-300" />
                  <h3 className="font-bold text-yellow-200 text-lg">
                    {language === 'pl' ? 'KIEDY' : 'WHEN'}
                  </h3>
                </div>
                <p className="text-blue-100 text-sm leading-relaxed">
                  {language === 'pl' 
                    ? 'Ferie zimowe (2–13 lutego 2026). To ostatni moment na kompleksowe uporządkowanie materiału przed finalną prostą.'
                    : 'Winter Break (February 2–13, 2026). This is the last moment for comprehensive material organization before the final stretch.'}
                </p>
              </div>
              
              <div className="bg-gradient-to-br from-blue-400/20 to-blue-600/20 backdrop-blur-sm rounded-xl p-4 border border-blue-300/30">
                <div className="flex items-center space-x-3 mb-3">
                  <MapPin className="w-6 h-6 text-blue-300" />
                  <h3 className="font-bold text-blue-200 text-lg">
                    {language === 'pl' ? 'GDZIE' : 'WHERE'}
                  </h3>
                </div>
                <p className="text-blue-100 text-sm leading-relaxed">
                  {language === 'pl'
                    ? 'Nowoczesne i komfortowe sale wykładowe WorldLINK Foundation Poland (Kraków-Wieliczka).'
                    : 'Modern and comfortable lecture halls at WorldLINK Foundation Poland (Krakow-Wieliczka).'}
                </p>
              </div>
              
              <div className="bg-gradient-to-br from-green-400/20 to-green-600/20 backdrop-blur-sm rounded-xl p-4 border border-green-300/30">
                <div className="flex items-center space-x-3 mb-3">
                  <Users className="w-6 h-6 text-green-300" />
                  <h3 className="font-bold text-green-200 text-lg">
                    {language === 'pl' ? 'JAK' : 'HOW'}
                  </h3>
                </div>
                <p className="text-blue-100 text-sm leading-relaxed">
                  {language === 'pl'
                    ? 'Wyłącznie zajęcia stacjonarne. Wierzymy w siłę interakcji – bezpośredni kontakt z MEGA DOŚWIADCZONYMI nauczycielami i wspólna praca w grupach dają efekty, których nie zastąpi żaden kurs online.'
                    : 'Exclusively in-person classes. We believe in the power of interaction – direct contact with MEGA EXPERIENCED teachers and collaborative group work deliver results that no online course can replace.'}
                </p>
              </div>
            </div>
            <p className="text-2xl md:text-3xl font-bold mb-8 text-yellow-200">
              {t.hero.motto}
            </p>
            <a 
              href="https://forms.gle/Ncctf4rbqzSmtpkc9" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center bg-white text-blue-600 hover:bg-blue-50 text-lg px-8 py-3 rounded-lg font-semibold transition-colors"
            >
              {t.hero.cta}
              <ChevronRight className="w-5 h-5 ml-2" />
            </a>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-white to-transparent"></div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-16 text-gray-900">{t.features.title}</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {t.features.items.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon className="w-8 h-8 text-blue-600" />
                    </div>
                    <CardTitle className="text-xl">{feature.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600">{feature.description}</p>
                    <div className="mt-4 text-xs text-gray-400 font-medium">
                      #EducationExcellence #QualityTourism #TimeLeverage #ValuesDrivenEducation #TrustInTeaching #SustainablePerformance
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Courses Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 text-gray-900">{t.courses.title}</h2>
            <p className="text-xl text-gray-600">{t.courses.subtitle}</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {t.courses.items.map((course, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex justify-between items-start mb-2">
                    <CardTitle className="text-xl">{course.name}</CardTitle>
                    <Badge variant="secondary">{course.date}</Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 mb-4">{course.description}</p>
                  <div className="flex items-center text-sm text-gray-500">
                    <Clock className="w-4 h-4 mr-1" />
                    <span>2.5 {language === 'pl' ? 'dni' : 'days'}</span>
                    <Users className="w-4 h-4 ml-4 mr-1" />
                    <span>{language === 'pl' ? 'max 10 osób' : 'max 10 people'}</span>
                  </div>
                  <div className="mt-4 text-xs text-gray-400 font-medium">
                    #EducationExcellence #QualityTourism #TimeLeverage #ValuesDrivenEducation #TrustInTeaching #SustainablePerformance
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Experts Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 text-gray-900">
              {language === 'pl' ? 'Nasi Eksperci' : 'Our Experts'}
            </h2>
            <p className="text-xl text-gray-600">
              {language === 'pl' 
                ? 'Poznaj doświadczonych nauczycieli, którzy poprowadzą Cię do sukcesu'
                : 'Meet the experienced teachers who will guide you to success'}
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {/* Dr David O'Keeffe */}
            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="w-16 h-16 rounded-full overflow-hidden mx-auto mb-4">
                  <img 
                    src="./images/David.jpeg" 
                    alt="dr David O'Keeffe" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardTitle className="text-xl text-center">dr David O'Keeffe</CardTitle>
                <CardDescription className="text-center">
                  {language === 'pl' ? 'Biologia SL & HL' : 'Biology SL & HL'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-center">
                  {language === 'pl'
                    ? '18+ doświadczenie w nauczaniu Biologii i Chemii IB DP czyni go wybitnym ekspertem w przygotowaniu uczniów do matury międzynarodowej IB oraz na studia medyczne. Jako doświadczony mentor z sukcesami prowadzi kandydatów na elitarne brytyjskie uczelnie, w tym na Oxford i Cambridge. Specjalizuje się w biologii molekularnej oraz ekologii, przekazując rzetelna wiedzę w sposób przystępny i pełen pasji. W swojej pracy stawia na partnerską relację z uczniem, wierząc, że przy odpowiednim wsparciu każdy może osiągnąć spektakularny progres. Dzięki połączeniu naukowego profesjonalizmu z poczuciem humoru tworzy inspirującą przestrzeń, w której nauka staje się prawdziwą przygodą. Jego podejście skutecznie otwiera drzwi do najbardziej prestiżowych uniwersytetów na świecie.'
                    : '18+ years of experience in teaching IB DP Biology and Chemistry makes him an outstanding expert in preparing students for the IB international baccalaureate and medical studies. As an experienced mentor, he successfully guides candidates to elite British universities, including Oxford and Cambridge. He specializes in molecular biology and ecology, conveying reliable knowledge in an accessible and passionate way. In his work, he focuses on a partnership relationship with the student, believing that with appropriate support, everyone can achieve spectacular progress. Thanks to combining scientific professionalism with a sense of humor, he creates an inspiring space where learning becomes a real adventure. His approach effectively opens doors to the most prestigious universities in the world.'}
                </p>
              </CardContent>
            </Card>
            
            {/* Dr Bill Roberts */}
            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="w-16 h-16 rounded-full overflow-hidden mx-auto mb-4">
                  <img 
                    src="./images/Bill.jpg" 
                    alt="dr Bill Roberts" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardTitle className="text-xl text-center">dr Bill Roberts</CardTitle>
                <CardDescription className="text-center">
                  {language === 'pl' ? 'Matematyka IBDP i TOK' : 'Mathematics IBDP and TOK'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-center">
                  {language === 'pl'
                    ? '30+ lat doświadczenia w nauczaniu matematyki IB oraz dyplom prestiżowego Uniwersytetu w Oxfordzie czynią Billa Robertsa światowej klasy ekspertem w swojej dziedzinie. Jako starszy egzaminator i lider zespołu IB (Senior Examiner), posiada unikatną wiedzę o standardach oceniania kursów AA na poziomach SL i HL. W swojej pracy skupia się na budowaniu pewności siebie uczniów oraz rozwijaniu umiejętności logicznego myślenia, co pozwala im z sukcesem dostrzegać złożone matematyczne wzorce. Ten wybitny mentor i doradca programowy łączy rzetelność akademicką z globalnym doświadczeniem, skutecznie wspierając młodzież w drodze na najlepsze uniwersytety świata.'
                    : '30+ years of experience in IB mathematics teaching and a degree from the prestigious University of Oxford make Bill Roberts a world-class expert in his field. As a Senior Examiner and IB team leader, he possesses unique knowledge of AA course assessment standards at SL and HL levels. In his work, he focuses on building student confidence and developing logical thinking skills, enabling them to successfully recognize complex mathematical patterns. This outstanding mentor and curriculum advisor combines academic rigor with global experience, effectively supporting young people on their path to the world\'s best universities.'}
                </p>
              </CardContent>
            </Card>
            
            {/* mgr Anna Krzemińska-Kaczyńska */}
            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="w-16 h-16 rounded-full overflow-hidden mx-auto mb-4">
                  <img 
                    src="./images/IMG_7249.jpg"
                    alt="mgr Anna Krzemińska-Kaczyńska" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardTitle className="text-xl text-center">mgr Anna Krzemińska-Kaczyńska</CardTitle>
                <CardDescription className="text-center">
                  {language === 'pl' ? 'Geografia IBDP' : 'Geography IBDP'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-center">
                  {language === 'pl'
                    ? '21+ lat doświadczenia w szkołach z maturą polską i międzynarodową IB pozwala jej łączyć rzetelność akademicką z najwyższym standardem nauczania Geografii IBDP. Laureatka prestiżowej Global Teacher Award, uhonorowana została Medalem KEN. Doświadczenie to uzupełniają kwalifikacje zdobyte w ramach programu stypendialnego TEA Departamentu Stanu USA. Jej autorskie metody pracy gwarantują uczniom sukces na najlepszych uczelniach oraz w przyszłej karierze zawodowej'
                    : '21+ years of experience in schools with Polish and international IB graduation allows her to combine academic rigor with the highest standard of Geography IBDP teaching. Winner of the prestigious Global Teacher Award, she was honored with the Medal of the National Education Commission. This experience is complemented by qualifications obtained through the TEA scholarship program of the US State Department. Her original teaching methods guarantee students success at the best universities and in their future professional careers.'}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Additional Experts Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8">
              {/* Anna Przybyło */}
              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-center space-x-4">
                    <div className="w-16 h-16 rounded-full overflow-hidden">
                      <img 
                        src="./images/Anna Przybylo.jpg"
                        alt="mgr Anna Przybyło" 
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <CardTitle className="text-xl">mgr Anna Przybyło</CardTitle>
                      <CardDescription>English B / English A Lang & Lit / CAS</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 mb-4">
                    {language === 'pl'
                      ? 'Doświadczona nauczycielka English B oraz Literature, koordynatorka CAS i metodyk. Z pasją wspiera sukcesy uczniów na maturze i w konkursach przedmiotowych, łącząc rzetelność naukową z kreatywnym podejściem do nauki języka.'
                      : 'Experienced English B and Literature teacher, CAS coordinator and methodologist. Passionately supports student success in graduation and subject competitions, combining scientific reliability with a creative approach to language learning.'}
                  </p>
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <p className="text-blue-800 text-sm font-semibold mb-2">
                      English B, CAS:
                    </p>
                    <p className="text-blue-700 text-sm">
                      {language === 'pl'
                        ? '* Luty 2026: 2.02, 4.02, 6.02 oraz 9.02\nGodziny: 17:00 – 19:00'
                        : '* February 2026: 2.02, 4.02, 6.02 and 9.02\nHours: 17:00 – 19:00'}
                    </p>
                  </div>
                  <div className="mt-4 text-xs text-gray-400 font-medium">
                    #EducationExcellence #QualityTourism #TimeLeverage #ValuesDrivenEducation #TrustInTeaching #SustainablePerformance
                  </div>
                </CardContent>
              </Card>
              
              {/* Monika Lis */}
              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-center space-x-4">
                    <div className="w-16 h-16 rounded-full overflow-hidden">
                      <img 
                        src="./images/Monika.jpeg" 
                        alt="dr Monika Lis" 
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <CardTitle className="text-xl">dr Monika Lis</CardTitle>
                      <CardDescription>
                        Polish A: Literature (2-13.02.2026, 17:00-19:00)
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">
                    {language === 'pl'
                      ? 'Doktor nauk humanistycznych, specjalistka w zakresie literatury polskiej. Ekspertka w przygotowaniu uczniów do wymagających egzaminów IB Polish A, kładąca nacisk na głęboką analizę tekstów oraz precyzję w pisaniu prac maturalnych.'
                      : 'Doctor of Humanities, specialist in Polish literature. Expert in preparing students for demanding IB Polish A exams, emphasizing deep text analysis and precision in writing graduation papers.'}
                  </p>
                  <div className="mt-4 text-xs text-gray-400 font-medium">
                    #EducationExcellence #QualityTourism #TimeLeverage #ValuesDrivenEducation #TrustInTeaching #SustainablePerformance
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Individual Sessions Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold mb-4 text-gray-900">
                {language === 'pl' ? 'Wsparcie Indywidualne' : 'Individual Support & Afternoon Sessions'}
              </h2>
              <p className="text-xl text-gray-600">
                {language === 'pl' ? (
                  'Oprócz zajęć grupowych, oferujemy możliwość rezerwacji indywidualnych sesji konsultacyjnych z kursów: Mathematics AA SL-HL, Biology SL-HL, TOK, Geography SL-HL, English B, Polish Literature A, CAS.'
                ) : (
                  'In addition to group classes, we offer the possibility to book individual consultation sessions for courses: Mathematics AA SL-HL, Biology SL-HL, TOK, Geography SL-HL, English B, Polish Literature A, CAS.'
                )}
              </p>
            </div>
            
            {/* Pricing and Contact */}
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <Card className="bg-blue-50 border-blue-200">
                <CardHeader>
                  <CardTitle className="text-xl text-blue-900">
                    {language === 'pl' ? 'Zrealizuj z nami najważniejsze cele' : 'Achieve your key objectives with us'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-blue-800 leading-relaxed">
                    {language === 'pl'
                      ? 'Zyskaj pewność siebie i powalcz o maksymalny wynik na 100 dni przed maturą międzynarodową IB. Nasze intensywne kursy to klucz do sukcesu na egzaminach końcowych.'
                      : 'Build your confidence and aim for a maximum score 100 days before the IB Diploma Programme exams. Our intensive courses are your key to success in the final assessments.'}
                  </p>
                  <div className="mt-4 text-xs text-blue-600 font-medium">
                    #EducationExcellence #QualityTourism #TimeLeverage #ValuesDrivenEducation #TrustInTeaching #SustainablePerformance
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-amber-50 border-amber-200">
                <CardHeader>
                  <CardTitle className="text-xl text-amber-900">
                    {language === 'pl' ? 'Jak zarezerwować?' : 'How to book?'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-amber-800 mb-4">
                    {language === 'pl'
                      ? 'Chcesz zarezerwować sesję indywidualną? Wypełnij formularz rejestracyjny poniżej.'
                      : 'Want to book an individual session? Fill out the registration form below.'}
                  </p>
                  
                  <div className="mb-4">
                    <a 
                      href="https://forms.gle/k7WADWvi6ob94LxS6" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-4 py-2 bg-amber-600 text-white font-semibold rounded-lg hover:bg-amber-700 transition-colors"
                    >
                      {language === 'pl' ? 'Formularz rejestracyjny' : 'Registration Form'}
                    </a>
                  </div>
                  
                  <div className="bg-amber-100 p-3 rounded-lg">
                    <p className="text-amber-900 font-semibold text-sm">
                      {language === 'pl'
                        ? '⚠️ Liczba miejsc na konsultacje 1:1 jest ściśle ograniczona.'
                        : '⚠️ The number of places for 1:1 consultations is strictly limited.'}
                    </p>
                  </div>
                  <div className="mt-4 text-xs text-amber-600 font-medium">
                    #EducationExcellence #QualityTourism #TimeLeverage #ValuesDrivenEducation #TrustInTeaching #SustainablePerformance
                  </div>
                </CardContent>
              </Card>
            </div>
            
            
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-blue-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">
            {language === 'pl' ? 'Gotowy na przyspieszenie?' : 'Ready to accelerate?'}
          </h2>
          <p className="text-xl mb-8 text-blue-100">
            {language === 'pl' 
              ? 'Zarejestruj się i uporządkuj wiedzę, zyskując kluczową pewność siebie na 100 dni przed egzaminami!' 
              : 'Register now and organize your knowledge, gaining crucial confidence 100 days before exams!'}
          </p>
          <a 
            href="https://forms.gle/Ncctf4rbqzSmtpkc9" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center bg-white text-blue-600 hover:bg-blue-50 text-lg px-8 py-3 rounded-lg font-semibold transition-colors"
          >
            {language === 'pl' ? 'Zapisz się teraz' : 'Register Now'}
            <ChevronRight className="w-5 h-5 ml-2" />
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-yellow-500 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-sm">IB</span>
                </div>
                <span className="font-bold text-xl">DP Accelerator</span>
              </div>
              <p className="text-gray-400">
                {language === 'pl' 
                  ? 'Intensywne kursy przygotowawcze do matury międzynarodowej IB.' 
                  : 'Intensive preparation courses for the International Baccalaureate.'}
              </p>
            </div>
            <div>
              <h3 className="font-bold mb-4">{language === 'pl' ? 'Kontakt' : 'Contact'}</h3>
              <div className="space-y-2 text-gray-400">
                <div className="flex items-center space-x-2">
                  <MapPin className="w-4 h-4" />
                  <span>{language === 'pl' ? 'Siedziba WorldLINK Foundation, Poland - Kraków near Wieliczka' : 'WorldLINK Foundation Headquarters, Poland - Krakow near Wieliczka'}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Calendar className="w-4 h-4" />
                  <span>2–13 {language === 'pl' ? 'lutego' : 'February'} 2026</span>
                </div>
                <div className="mt-4 pt-4 border-t border-gray-700">
                  <div className="flex items-start space-x-2">
                    <User className="w-4 h-4 mt-1" />
                    <div>
                      <p className="font-semibold text-white">Mariana Nadolska</p>
                      <p className="text-sm">Poland</p>
                      <p className="text-sm">Business Projects Advisor</p>
                      <div className="flex items-center space-x-2 mt-1">
                        <Mail className="w-3 h-3" />
                        <span className="text-sm">worldlinkfoundation@gmail.com</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <h3 className="font-bold mb-4">{language === 'pl' ? 'Kursy' : 'Courses'}</h3>
              <ul className="space-y-2 text-gray-400">
                <li>Biology SL & HL</li>
                <li>Geography SL & HL</li>
                <li>Mathematics AA SL & HL</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center">
            <p className="text-lg font-semibold text-white mb-4">
              {language === 'pl' 
                ? 'Masz pytania? Chętnie odpowiemy na sesji 1:1'
                : 'Have questions? We\'ll be happy to answer in a 1:1 session'}
            </p>
            <p className="text-gray-400">&copy; 2026 IB DP Accelerator. {language === 'pl' ? 'Wszystkie prawa zastrzeżone.' : 'All rights reserved.'}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Index;
